// Thin HTTP client for the backend. All endpoints live under /api/*. The
// Vite dev server proxies /api -> uvicorn; in prod both share one origin, so
// the same relative URLs work either way.

export interface AuthUser {
  username: string;
  is_admin: boolean;
  must_change_password?: boolean;
}

export interface Camera {
  id: number;
  slug: string;
  name: string;
  url?: string;        // present only in the admin catalog
  transcode?: boolean; // admin catalog: re-encode to 8-bit H.264
  mirror?: boolean;    // admin catalog: flip left-right (needs transcode)
}

let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

async function errMsg(r: Response, fallback: string): Promise<string> {
  try {
    const j = await r.json();
    if (j && typeof j.detail === "string") return j.detail;
  } catch { /* ignore */ }
  return `${fallback} (${r.status})`;
}

/** Shared wrapper for every AUTHENTICATED request: a 401 means the session
 *  lapsed mid-use, so trigger logout-to-login consistently everywhere. (The
 *  login and getMe calls handle 401 themselves and don't use this.) */
async function request(path: string, init: RequestInit, fallback: string): Promise<Response> {
  const r = await fetch(path, init);
  if (r.status === 401) {
    onUnauthorized?.();
    throw new Error("session expired — please log in again");
  }
  if (!r.ok) throw new Error(await errMsg(r, fallback));
  return r;
}

const JSON_HEADERS = { "Content-Type": "application/json" };

// --- auth -------------------------------------------------------------------

export async function getMe(): Promise<AuthUser | null> {
  const r = await fetch("/api/auth/me");
  if (r.status === 401) return null;
  if (!r.ok) throw new Error(`me: ${r.status}`);
  return r.json();
}

export async function login(username: string, password: string): Promise<AuthUser> {
  const r = await fetch("/api/auth/login", {
    method: "POST", headers: JSON_HEADERS,
    body: JSON.stringify({ username, password }),
  });
  if (!r.ok) throw new Error(await errMsg(r, "login failed"));
  return r.json();
}

export async function logout(): Promise<void> {
  await fetch("/api/auth/logout", { method: "POST" });
}

export async function changePassword(old_password: string, new_password: string): Promise<void> {
  await request("/api/auth/change-password", {
    method: "POST", headers: JSON_HEADERS,
    body: JSON.stringify({ old_password, new_password }),
  }, "change failed");
}

// --- cameras + layout (viewer) ---------------------------------------------

export async function listCameras(): Promise<Camera[]> {
  const r = await request("/api/cameras", {}, "list cameras failed");
  return (await r.json()).cameras;
}

export async function getLayout(): Promise<(string | null)[]> {
  const r = await request("/api/layout", {}, "load layout failed");
  return (await r.json()).slots;
}

export async function saveLayout(slots: (string | null)[]): Promise<void> {
  await request("/api/layout", {
    method: "PUT", headers: JSON_HEADERS, body: JSON.stringify({ slots }),
  }, "save layout failed");
}

/** WebSocket URL of the auth-gated MSE proxy for a stream slug. */
export function streamWsUrl(slug: string): string {
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${location.host}/api/stream/${encodeURIComponent(slug)}/ws`;
}

// --- admin: users -----------------------------------------------------------

export interface AdminUser { username: string; is_admin: boolean; }

export async function adminListUsers(): Promise<AdminUser[]> {
  const r = await request("/api/admin/users", {}, "list users failed");
  return (await r.json()).users;
}

export async function adminCreateUser(
  username: string, password: string, is_admin = false,
): Promise<void> {
  await request("/api/admin/users", {
    method: "POST", headers: JSON_HEADERS,
    body: JSON.stringify({ username, password, is_admin }),
  }, "create failed");
}

export async function adminResetPassword(username: string, password: string): Promise<void> {
  await request(`/api/admin/users/${encodeURIComponent(username)}/reset-password`, {
    method: "POST", headers: JSON_HEADERS, body: JSON.stringify({ password }),
  }, "reset failed");
}

export async function adminDeleteUser(username: string): Promise<void> {
  await request(`/api/admin/users/${encodeURIComponent(username)}`, {
    method: "DELETE",
  }, "delete failed");
}

// --- admin: cameras ---------------------------------------------------------

export async function adminListCameras(): Promise<Camera[]> {
  const r = await request("/api/admin/cameras", {}, "list cameras failed");
  return (await r.json()).cameras;
}

export async function adminCreateCamera(
  name: string, url: string, transcode = false, mirror = false,
): Promise<Camera> {
  const r = await request("/api/admin/cameras", {
    method: "POST", headers: JSON_HEADERS, body: JSON.stringify({ name, url, transcode, mirror }),
  }, "create camera failed");
  return r.json();
}

export async function adminUpdateCamera(
  id: number, name: string, url: string, transcode = false, mirror = false,
): Promise<Camera> {
  const r = await request(`/api/admin/cameras/${id}`, {
    method: "PUT", headers: JSON_HEADERS, body: JSON.stringify({ name, url, transcode, mirror }),
  }, "update camera failed");
  return r.json();
}

export async function adminDeleteCamera(id: number): Promise<void> {
  await request(`/api/admin/cameras/${id}`, { method: "DELETE" }, "delete camera failed");
}
